import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { doc, setDoc, getDoc, collection, runTransaction, getDocs, query, where, updateDoc, arrayUnion } from 'firebase/firestore'
import { useLocation, useNavigate } from 'react-router-dom'
import { db, functions } from '../firebaseConfig'
import Select from 'react-select'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons'
import { useTheme } from '../context/ThemeContext'
import { BS_UNIT_CODES } from '../constants/businessUnits'
import { BS_LAMPIRAN_THRESHOLD, requiresBsLampiran } from '../constants/bonSementara'
import { uploadPdfFile } from '../utils/uploadPdfFile'
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_SIZE_BYTES, isValidAttachmentFile, mergeAttachmentsToPdf } from '../utils/attachmentUpload'

const FormBs = () => {
    const { theme } = useTheme()
    const location = useLocation()
    const navigate = useNavigate()
    const isEditMode = location.state?.isEditMode || false
    const editData = location.state?.editData || null

    const [todayDate, setTodayDate] = useState('')
    const [alreadyFetchBS, setAlreadyFetchBS] = useState(false)
    const [userData, setUserData] = useState({
        uid: '',
        nama: '',
        bankName: '',
        accountNumber: '',
        unit: [], // Sekarang array
        posisi: '',
        reviewer1: [],
        reviewer2: []
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isUserDataLoaded, setIsUserDataLoaded] = useState(false)
    // Bagian AN: catatan wajib diisi editor (Admin/Validator/Reviewer/Super
    // Admin) saat mengedit pengajuan MILIK ORANG LAIN -- jadi jejak "kesalahan
    // apa yang diperbaiki" tersimpan di statusHistory, bukan cuma teks generik
    // "Super Admin mengedit detail form ...".
    const [editNote, setEditNote] = useState('')
    // Bagian BO: lampiran wajib untuk BS > Rp3 juta (BS_LAMPIRAN_THRESHOLD).
    const [attachmentFiles, setAttachmentFiles] = useState([])

    const initialBonSementaraState = {
        nomorBS: '',
        aktivitas: '',
        jumlahBS: '',
        kategori: '',
        tanggalPengajuan: todayDate
    }

    const [bonSementara, setBonSementara] = useState([initialBonSementaraState])

    useEffect(() => {
        if (todayDate) {
            setBonSementara((prevBonSementara) =>
                prevBonSementara.map((item) => ({ ...item, tanggalPengajuan: todayDate }))
            )
        }
    }, [todayDate])

    const [selectedUnit, setSelectedUnit] = useState(null)
    const [userUnitOptions, setUserUnitOptions] = useState([])
    const [isAdmin, setIsAdmin] = useState(false)

    const [reviewerOptions, setReviewerOptions] = useState([])
    const [selectedReviewer1, setSelectedReviewer1] = useState(null)
    const [selectedReviewer2, setSelectedReviewer2] = useState(null)

    // Deteksi apakah user hanya memiliki 1 unit bisnis
    const isSingleUnit = !isAdmin && userUnitOptions.length === 1

    // Fetch reviewer untuk semua role
    useEffect(() => {
        const fetchReviewer = async () => {
            try {
                const usersRef = collection(db, 'userDirectory')
                const q = query(usersRef, where('role', 'in', ['Reviewer']))
                const querySnapshot = await getDocs(q)

                const options = querySnapshot.docs.map((doc) => {
                    const userData = doc.data()
                    return {
                        value: userData.uid,
                        label: userData.nama,
                        role: userData.role
                    }
                })

                setReviewerOptions(options)
            } catch (error) {
                console.error('Error fetching reviewers:', error)
                toast.error('Gagal memuat daftar reviewer')
            }
        }

        fetchReviewer()
    }, [])

    const BUSINESS_UNITS = [
        { value: 'PT Makassar Jaya Samudera', label: 'PT Makassar Jaya Samudera' },
        { value: 'PT Samudera Makassar Logistik', label: 'PT Samudera Makassar Logistik' },
        { value: 'PT Kendari Jaya Samudera', label: 'PT Kendari Jaya Samudera' },
        { value: 'PT Samudera Kendari Logistik', label: 'PT Samudera Kendari Logistik' },
        { value: 'PT Samudera Agencies Indonesia', label: 'PT Samudera Agencies Indonesia' },
        { value: 'PT SILKargo Indonesia', label: 'PT SILKargo Indonesia' },
        { value: 'PT PAD Samudera Perdana', label: 'PT PAD Samudera Perdana' },
        { value: 'PT Masaji Kargosentra Tama', label: 'PT Masaji Kargosentra Tama' },
        { value: 'Samudera Indonesia', label: 'Samudera Indonesia' },
        { value: 'Panitia', label: 'Panitia' }
    ]

    // --- FUNGSI FETCH USER DATA (Pisahkan dari Hook agar bersih) ---
    const fetchUserData = useCallback(async (uid) => {
        // Pintu penjaga: Jika mode edit, jangan ambil data user login
        if (isEditMode && editData) {
            setIsUserDataLoaded(true);
            return;
        }

        try {
            const userDocRef = doc(db, 'users', uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
                const data = userDoc.data();
                const adminStatus = data.role === 'Admin' || data.role === 'Super Admin';
                setIsAdmin(adminStatus);

                const userUnitsArray = Array.isArray(data.unit) ? data.unit : (data.unit ? [data.unit] : []);

                setUserData({
                    uid: data.uid || '',
                    nama: data.nama || '',
                    bankName: data.bankName || '',
                    accountNumber: data.accountNumber || '',
                    unit: userUnitsArray,
                    posisi: data.posisi || '',
                    department: data.department || [],
                    reviewer1: data.reviewer1 || [],
                    reviewer2: data.reviewer2 || []
                });

                const unitOptionsForUser = userUnitsArray.map(u => ({ value: u, label: u }));
                setUserUnitOptions(unitOptionsForUser);

                if (!adminStatus && unitOptionsForUser.length === 1) {
                    setSelectedUnit(unitOptionsForUser[0]);
                } else if (!adminStatus && unitOptionsForUser.length === 0) {
                    setSelectedUnit(null);
                }

                setIsUserDataLoaded(true);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            toast.error('Error fetching user data');
        }
    }, [isEditMode, editData]);

// --- HOOK 1: Inisialisasi Awal (Hanya jalan sekali saat mount) ---
    useEffect(() => {
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0];
        setTodayDate(formattedDate);

        const uid = localStorage.getItem('userUid');
        if (uid) {
            fetchUserData(uid); // Kirimkan uid ke fungsi fetch
        }
    }, [fetchUserData]);

    // --- HOOK 2: BLOK AUTO-FILL (BERDIRI SENDIRI DI LUAR) ---
    // Hook ini akan jalan setiap kali editData atau reviewerOptions berubah
    useEffect(() => {
        if (isEditMode && editData) {
            // 1. Set Data User Pengaju Asli
            if (editData.user) {
                setUserData({
                    uid: editData.user.uid || '',
                    nama: editData.user.nama || '',
                    bankName: editData.user.bankName || '',
                    accountNumber: editData.user.accountNumber || '',
                    unit: [editData.user.unit], 
                    posisi: editData.user.posisi || '',
                    department: editData.user.department || [],
                    reviewer1: editData.user.reviewer1 || [],
                    reviewer2: editData.user.reviewer2 || []
                });

                setSelectedUnit({ value: editData.user.unit, label: editData.user.unit });
                
                if (reviewerOptions.length > 0) {
                    const rev1 = reviewerOptions.find(opt => editData.user.reviewer1.includes(opt.value));
                    const rev2 = reviewerOptions.find(opt => editData.user.reviewer2.includes(opt.value));
                    if (rev1) setSelectedReviewer1(rev1);
                    if (rev2) setSelectedReviewer2(rev2);
                }
            }

            // 2. Set Item Bon Sementara
            if (editData.bonSementara && Array.isArray(editData.bonSementara)) {
                const formattedBS = editData.bonSementara.map(item => ({
                    ...item,
                    jumlahBS: item.jumlahBS?.toString() || '',
                    kategori: item.kategori || ''
                }));
                setBonSementara(formattedBS);

                if (formattedBS[0]?.kategori) {
                    setSelectedKategori({ value: formattedBS[0].kategori, label: formattedBS[0].kategori });
                }
            }
            
            setIsUserDataLoaded(true);
        }
    }, [isEditMode, editData, reviewerOptions]);
    
    // --- Logika Auto-Fill Reviewer 1 dan 2 untuk user dengan 1 Unit Bisnis ---
    useEffect(() => {
        if (isSingleUnit) {
            // Auto-fill Reviewer 1
            if (reviewerOptions.length > 0 && userData.reviewer1?.length > 0) {
                const defaultRev1 = reviewerOptions.find(opt => userData.reviewer1.includes(opt.value));
                if (defaultRev1) setSelectedReviewer1(defaultRev1);
            }
            
            // Auto-fill Reviewer 2
            if (reviewerOptions.length > 0 && userData.reviewer2?.length > 0) {
                const defaultRev2 = reviewerOptions.find(opt => userData.reviewer2.includes(opt.value));
                if (defaultRev2) setSelectedReviewer2(defaultRev2);
            }
        }
    }, [isSingleUnit, reviewerOptions, userData]);

    const kategoriOptions = useMemo(() => [
        { value: 'GA/Umum', label: 'GA/Umum' },
        { value: 'Marketing/Operasional', label: 'Marketing/Operasional' }
    ], [])

    const [selectedKategori, setSelectedKategori] = useState(null)

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date)
    }

    const handleKategoriChange = (selectedOption) => {
        setSelectedKategori(selectedOption)
        setBonSementara((prevBonSementara) =>
            prevBonSementara.map((item, index) => (index === 0 ? { ...item, kategori: selectedOption.value } : item))
        )
    }

    const isDark = theme === 'dark'
    const customStyles = {
        control: (base, state) => ({
            ...base,
            padding: '0 7px',
            height: '40px',
            minHeight: '40px',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            backgroundColor: state.isDisabled ? (isDark ? '#374151' : '#f9fafb') : (isDark ? '#1f2937' : 'white'),
            '&:hover': {
                borderColor: state.isDisabled ? (isDark ? '#4b5563' : '#e5e7eb') : '#3b82f6'
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 7px',
            height: '40px',
            minHeight: '40px'
        }),
        singleValue: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        input: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        placeholder: (base) => ({ ...base, color: isDark ? '#9ca3af' : '#6b7280' }),
        menu: (base) => ({ ...base, zIndex: 100, backgroundColor: isDark ? '#1f2937' : '#ffffff' }),
        // Bagian AX: menuPortalTarget={document.body} butuh z-index tinggi di sini
        // juga (bukan cuma di `menu`) supaya dropdown tidak tampil tertutup/salah
        // posisi kalau Select ini dirender di dalam modal/overlay lain.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: isDark
                ? (state.isSelected ? '#374151' : state.isFocused ? '#2d3748' : '#1f2937')
                : base.backgroundColor,
            color: isDark ? '#f3f4f6' : base.color,
            cursor: 'pointer'
        })
    }

    const resetForm = () => {
        setBonSementara([
            {
                ...initialBonSementaraState,
                tanggalPengajuan: todayDate
            }
        ])
        
        setSelectedKategori(null)
        setAttachmentFiles([])

        if (isAdmin || userUnitOptions.length > 1) {
            setSelectedUnit(null)
            setSelectedReviewer1(null)
            setSelectedReviewer2(null)
        }
    }

    const formatRupiah = (number) => {
        if (number === undefined || number === null) return ''
        const stringNumber = number.toString()
        const strNumber = stringNumber.replace(/[^,\d]/g, '')
        const split = strNumber.split(',')
        const sisa = split[0].length % 3
        let rupiah = split[0].substr(0, sisa)
        const ribuan = split[0].substr(sisa).match(/\d{3}/gi)

        if (ribuan) {
            const separator = sisa ? '.' : ''
            rupiah += separator + ribuan.join('.')
        }

        rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah
        return rupiah ? 'Rp' + rupiah : ''
    }

    const handleInputChange = (index, field, value) => {
        if (field === 'nomorBS') return // Hindari perubahan manual

        let formattedValue = value

        if (field === 'jumlahBS') {
            formattedValue = formatRupiah(value)
        }

        const updatedBonSementara = bonSementara.map((item, i) =>
            i === index ? { ...item, [field]: formattedValue } : item
        )
        setBonSementara(updatedBonSementara)
    }

    const needsLampiran = requiresBsLampiran(bonSementara)
    // Mode edit: BS lama yang sudah punya lampiran tidak wajib upload ulang.
    const hasExistingLampiran = Boolean(isEditMode && editData?.lampiranUrl)

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files)
        if (!files.length) return

        const validFiles = []
        for (const file of files) {
            if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
                toast.error(`Ukuran file ${file.name} maksimal ${Math.round(ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024))}MB`)
                continue
            }
            if (!(await isValidAttachmentFile(file))) {
                toast.error(`File ${file.name} bukan PDF/JPG/PNG yang valid`)
                continue
            }
            validFiles.push(file)
        }

        setAttachmentFiles(prev => [...prev, ...validFiles])
        event.target.value = ''
    }

    const removeAttachment = (indexToRemove) => {
        setAttachmentFiles(prev => prev.filter((_, index) => index !== indexToRemove))
    }

    // Lampiran digabung jadi 1 PDF (sama seperti RBS/LPJ) lalu diupload SEBELUM
    // transaksi nomor BS -- nomor final baru diketahui di dalam transaksi, jadi
    // path & kunci kepemilikan pakai token unik per-upload (`BSLAMPIRAN-...`)
    // yang tidak pernah terdaftar di /displayIdOwners, sehingga lolos mode
    // 'displayIdOwners' di uploadOwnedFile. URL-nya lalu ikut ditulis di
    // transaksi yang SAMA dengan dokumen BS, jadi tidak ada BS > 3 juta yang
    // tersimpan tanpa lampiran walau upload gagal di tengah jalan.
    const uploadBsLampiran = async (files, kategori) => {
        const uploadKey = `BSLAMPIRAN-${userData.uid}-${Date.now()}`
        const mergedFile = await mergeAttachmentsToPdf(files, 'Lampiran_BS.pdf')
        const sanitizedKategori = (kategori || 'Lainnya').replace(/\//g, '_')
        return uploadPdfFile(
            functions,
            `BonSementara/${sanitizedKategori}/Lampiran/${uploadKey}/${mergedFile.name}`,
            mergedFile,
            { mode: 'displayIdOwners', displayId: uploadKey }
        )
    }

    const getCurrentCounter = async (unitCode) => {
        try {
            const counterRef = doc(db, 'businessUnitCounters', unitCode);
            const counterDoc = await getDoc(counterRef);

            const today = new Date();
            const year = today.getFullYear().toString();

            if (!counterDoc.exists()) {
                await setDoc(counterRef, {
                    lastNumber: 500,
                    lastResetYear: year
                });
                return '0000501';
            }

            const lastResetYear = counterDoc.data().lastResetYear || '0000';

            if (lastResetYear !== year) {
                await setDoc(counterRef, {
                    lastNumber: 500,
                    lastResetYear: year
                });
                return '0000501';
            }

            const currentNumber = counterDoc.data().lastNumber;
            return (currentNumber + 1).toString().padStart(7, '0');
        } catch (error) {
            console.error('Error getting current counter:', error);
            return '0000501';
        }
    };

    const handleUnitChange = async (selectedOption) => {
        setSelectedUnit(selectedOption);

        // Bagian CG: koreksi dari user -- nomor BS berubah tiba-tiba setiap
        // kali admin mengedit pengajuan. Akar masalahnya: nomor yang
        // digenerate di sini CUMA PREVIEW (baca counter TANPA increment,
        // lihat catatan di handleSubmit bagian "LOGIKA JIKA BIKIN BARU") --
        // untuk pengajuan BARU, preview ini selalu dibuang & diganti nomor
        // FINAL yang digenerate ulang di dalam runTransaction saat submit,
        // jadi aman. TAPI mode EDIT tidak punya langkah regenerasi itu --
        // updateDoc mengirim `bonSementara` apa adanya dari state ini, jadi
        // preview yang seharusnya sementara malah ikut tersimpan PERMANEN
        // sebagai nomor BS baru, menimpa nomor asli, begitu dropdown Unit
        // Bisnis ini tersentuh (termasuk re-select unit yang sama).
        // Nomor BS yang sudah ada tidak boleh ikut berubah hanya karena
        // field lain diedit -- kalau admin memang perlu mengganti nomor BS
        // satu per satu, field "Nomor BS" di bawah tetap bisa diedit manual.
        if (isEditMode) return;

        if (!selectedOption) {
            setBonSementara(prev => prev.map(item => ({ ...item, nomorBS: '' })));
            return;
        }

        try {
            const today = new Date();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const year = today.getFullYear().toString().slice(-2);
            const tanggalKode = `${year}${month}`;

            const kodeUnitBisnis = BS_UNIT_CODES[selectedOption.value];

            if (!kodeUnitBisnis) {
                throw new Error(`No code found for business unit: ${selectedOption.value}`);
            }

            const sequence = await getCurrentCounter(kodeUnitBisnis);

            const newNomorBS = `BS${tanggalKode}${kodeUnitBisnis}${sequence}`;

            setBonSementara(prevBonSementara =>
                prevBonSementara.map(item => ({ ...item, nomorBS: newNomorBS }))
            );

        } catch (error) {
            console.error('Error generating new BS number:', error);
            toast.error('Error generating new BS number: ' + error.message);
        }
    };

    const generateNomorBS = useCallback(async () => {
        try {
            if (alreadyFetchBS) return null;

            if (!isUserDataLoaded) {
                return null;
            }

            const currentUnit = selectedUnit ? selectedUnit.value : (userData.unit.length === 1 ? userData.unit[0] : null);

            if (!currentUnit) {
                return null; // Tunggu sampai user pilih unit
            }

            setAlreadyFetchBS(true);

            const today = new Date();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const year = today.getFullYear().toString().slice(-2);
            const tanggalKode = `${year}${month}`;

            const kodeUnitBisnis = BS_UNIT_CODES[currentUnit];

            if (!kodeUnitBisnis) {
                throw new Error(`No code found for business unit: ${currentUnit}`);
            }

            const sequence = await getCurrentCounter(kodeUnitBisnis);

            const nomorBS = `BS${tanggalKode}${kodeUnitBisnis}${sequence}`;
            return nomorBS;

        } catch (error) {
            console.error('Error generating nomor BS:', error);
            toast.error('Error: ' + error.message);
            return null;
        }
    }, [alreadyFetchBS, isUserDataLoaded, selectedUnit, userData.unit]);

    useEffect(() => {
        const fetchNomorBS = async () => {
            if (!isUserDataLoaded || alreadyFetchBS || !selectedUnit) return

            const nomorBS = await generateNomorBS()
            if (nomorBS) {
                setBonSementara((prevBonSementara) =>
                    prevBonSementara.map((item, index) => (index === 0 ? { ...item, nomorBS: nomorBS } : item))
                )
            }
        }

        fetchNomorBS()
    }, [todayDate, alreadyFetchBS, isUserDataLoaded, generateNomorBS, selectedUnit])

    useEffect(() => {
        if (isEditMode && editData && editData.bonSementara) {
            
            // A. Set Data Bon Sementara
            const formattedBS = editData.bonSementara.map(item => ({
                ...item,
                jumlahBS: item.jumlahBS?.toString() || '',
                kategori: typeof item.kategori === 'string' ? item.kategori : item.kategori?.value || ''
            }));
            setBonSementara(formattedBS);

            // B. Set Dropdown Kategori
            if (formattedBS[0]?.kategori) {
                const matchedKategori = kategoriOptions.find(opt => opt.value === formattedBS[0].kategori);
                setSelectedKategori(matchedKategori || { value: formattedBS[0].kategori, label: formattedBS[0].kategori });
            }

            // C. Set Data User Pengaju Asli
            setTimeout(() => {
                if (editData.user) {
                    setUserData(prev => ({
                        ...prev,
                        uid: editData.user.uid,
                        nama: editData.user.nama,
                        bankName: editData.user.bankName || '',
                        accountNumber: editData.user.accountNumber || '',
                        posisi: editData.user.posisi || '',
                        department: editData.user.department || ''
                    }));

                    if (editData.user.unit) {
                        setSelectedUnit({ value: editData.user.unit, label: editData.user.unit });
                    }

                    const findOption = (options, val) => options.find(o => o.value === val) || { value: val, label: val };

                    if (editData.user.reviewer1 && editData.user.reviewer1.length > 0) {
                        setSelectedReviewer1(findOption(reviewerOptions, editData.user.reviewer1[0]));
                    }
                    if (editData.user.reviewer2 && editData.user.reviewer2.length > 0) {
                        setSelectedReviewer2(findOption(reviewerOptions, editData.user.reviewer2[0]));
                    }
                }
            }, 100);
        }
    }, [isEditMode, editData, reviewerOptions, kategoriOptions])

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true)

            if (selectedReviewer1 && selectedReviewer2 && selectedReviewer1.value === selectedReviewer2.value) {
                toast.warning('Reviewer 1 dan Reviewer 2 tidak boleh sama')
                setIsSubmitting(false)
                return
            }

            const missingFields = []

            if (!userData.nama) missingFields.push('Nama')
            if (!selectedUnit?.value) missingFields.push('Unit Bisnis')
            if (!selectedReviewer1) missingFields.push('Reviewer 1')
            if (!selectedReviewer2) missingFields.push('Reviewer 2')

            const multipleItems = bonSementara.length > 1

            bonSementara.forEach((r, index) => {
                const getFieldLabel = (baseLabel) => {
                    return multipleItems ? `${baseLabel} (Item ${index + 1})` : baseLabel
                }

                if (!r.nomorBS) missingFields.push(getFieldLabel('Nomor BS'))
                if (!r.jumlahBS) missingFields.push(getFieldLabel('Jumlah BS'))
                if (!r.kategori) missingFields.push(getFieldLabel('Kategori'))
                if (!r.aktivitas) missingFields.push(getFieldLabel('Aktivitas'))
            })

            if (isEditMode && !editNote.trim()) missingFields.push('Catatan Perubahan')
            if (needsLampiran && attachmentFiles.length === 0 && !hasExistingLampiran) {
                missingFields.push(`Lampiran (wajib untuk BS di atas ${formatRupiah(BS_LAMPIRAN_THRESHOLD)})`)
            }

            if (missingFields.length > 0) {
                missingFields.forEach((field) => {
                    toast.warning(
                        <>
                            Mohon lengkapi <b>{field}</b>
                        </>
                    )
                })

                setIsSubmitting(false)
                return
            }

            const displayId = bonSementara[0]?.nomorBS
            const kodeUnitBisnis = BS_UNIT_CODES[selectedUnit.value]

            const parseRupiah = (value) => {
                return Number(value.replace(/[^,\d]/g, '').replace(',', '.')) || 0
            }

            // Upload lampiran hanya kalau memang dibutuhkan (> 3 juta) -- file
            // yang sempat dipilih lalu nominalnya diturunkan tidak ikut terkirim.
            let lampiranData = {}
            if (needsLampiran && attachmentFiles.length > 0) {
                try {
                    const lampiranUrl = await uploadBsLampiran(attachmentFiles, selectedKategori?.value)
                    lampiranData = { lampiran: ['Lampiran_BS.pdf'], lampiranUrl }
                } catch (uploadError) {
                    console.error('Error uploading BS lampiran:', uploadError?.code, uploadError?.message, uploadError)
                    toast.error(uploadError?.message || 'Gagal mengunggah lampiran. BS belum diajukan, silakan coba lagi.')
                    setIsSubmitting(false)
                    return
                }
            }

            const bonSementaraData = {
                user: {
                    uid: userData.uid,
                    nama: userData.nama,
                    bankName: userData.bankName,
                    accountNumber: userData.accountNumber,
                    unit: selectedUnit.value,
                    posisi: userData.posisi,
                    department: userData.department,
                    reviewer1: [selectedReviewer1.value],
                    reviewer2: [selectedReviewer2.value]
                },
                bonSementara: bonSementara.map((item) => ({
                    nomorBS: item.nomorBS,
                    jumlahBS: parseRupiah(item.jumlahBS),
                    aktivitas: item.aktivitas,
                    kategori: item.kategori
                })),
                displayId: displayId,
                ...lampiranData,
                tanggalPengajuan: todayDate,
                status: 'Diajukan',
                approvedByReviewer1: false,
                approvedByReviewer2: false,
                approvedBySuperAdmin: false,
                rejectedBySuperAdmin: false,
                statusHistory: [
                    {
                        status: 'Diajukan',
                        timestamp: new Date().toISOString(),
                        actor: userData.uid
                    }
                ]
            }

            if (isEditMode) {
                // --- LOGIKA JIKA EDIT ---
                // Bagian AN: `userData.uid` di mode edit adalah uid PENGAJU ASLI
                // (diisi dari editData.user, lihat useEffect auto-fill di atas),
                // BUKAN uid editor yang sedang login -- pakai localStorage
                // langsung supaya statusHistory mencatat SIAPA SEBENARNYA yang
                // melakukan perubahan (bug lama: entri edit selalu tercatat
                // seolah-olah pengaju mengedit pengajuannya sendiri).
                const editorUid = localStorage.getItem('userUid')
                const editorRole = localStorage.getItem('userRole') || 'Admin'
                const bsRef = doc(db, 'bonSementara', editData.id)
                await updateDoc(bsRef, {
                    // Bagian AT: sebelumnya `user` (unit/reviewer1/reviewer2)
                    // TIDAK PERNAH ikut disertakan di sini -- dropdown
                    // Reviewer 1/2 tetap bisa diklik & diganti saat edit,
                    // tapi perubahannya SELALU dibuang diam-diam saat
                    // disimpan. firestore.rules keepsWorkflowIdentity() cuma
                    // menjaga user.uid & displayId tetap sama -- reviewer1/
                    // reviewer2 di dalam map user memang boleh berubah.
                    user: bonSementaraData.user,
                    bonSementara: bonSementaraData.bonSementara,
                    ...lampiranData,
                    statusHistory: arrayUnion({
                        status: `Data Diubah oleh ${editorRole}`,
                        timestamp: new Date().toISOString(),
                        actor: editorUid,
                        reason: editNote.trim()
                    })
                })
                
                toast.success('Bon Sementara berhasil diperbarui!')
                setIsSubmitting(false)
                navigate('/bon-sementara/cek-pengajuan') // Arahkan kembali ke tabel

            } else {
                // --- LOGIKA JIKA BIKIN BARU ---
                // PENTING: nomor BS final HARUS di-generate di dalam transaksi yang sama
                // dengan increment counter. Nomor yang tampil saat pilih Unit Bisnis
                // (generateNomorBS/handleUnitChange) hanya PREVIEW dari pembacaan counter
                // tanpa increment - kalau dua user submit hampir bersamaan dengan unit
                // yang sama, preview mereka bisa sama persis dan nomor duplikat itu yang
                // ikut tersimpan. Dengan menggenerate nomor final di sini, di dalam
                // runTransaction, Firestore akan otomatis retry transaksi jika terjadi
                // konflik baca-tulis pada counter, sehingga tidak mungkin dua submit
                // mendapat nomor akhir yang sama.
                const counterRef = doc(db, 'businessUnitCounters', kodeUnitBisnis)

                // Bagian AO/AP: counter TIDAK SELALU sinkron dengan nomor yang
                // benar-benar sudah terpakai -- mis. dokumen lama dari sebelum
                // sistem counter atomik ini ada, yang sudah ditautkan ke
                // /displayIdOwners lewat callable backfillDisplayIdOwners()
                // (functions/index.js) tapi TANPA ikut memajukan
                // businessUnitCounters. Kalau nomor yang dihasilkan counter
                // ternyata sudah dipakai, transaction.set() ke
                // displayIdOwners dievaluasi sebagai "update" (dokumennya
                // sudah ada) -- rule koleksi itu `allow update: if false`,
                // jadi transaksi gagal permission-denied dengan nomor yang
                // SAMA setiap kali (counter tidak pernah maju karena
                // transaksinya sendiri gagal, jadi baca ulang counter di
                // percobaan berikutnya menghasilkan angka yang sama lagi).
                //
                // Percobaan sebelumnya (Bagian AO) mencoba CEK LEBIH DULU ke
                // displayIdOwners pakai transaction.get() -- ternyata rule
                // koleksi itu JUGA melarang get/list (`allow get, list: if
                // false`, cuma create yang boleh, murni blind-write by
                // design), jadi cek itu sendiri yang kena permission-denied.
                // Solusi yang benar: TIDAK membaca displayIdOwners sama
                // sekali -- coba tulis, kalau transaksi ditolak karena nomor
                // itu ternyata sudah dipakai, tangkap error-nya DI LUAR
                // transaksi lalu ulangi dengan offset nomor berikutnya
                // (counter dibaca ulang tiap percobaan, tapi offset manual
                // yang menjamin progres walau counter tersimpan tidak maju).
                // Begitu 1 percobaan berhasil, counter otomatis "sembuh"
                // (tersimpan sama dengan nomor yang benar-benar terpakai).
                const attemptGenerateBs = async (offset) => {
                    const newDocRef = doc(collection(db, 'bonSementara'))
                    return runTransaction(db, async (transaction) => {
                        const counterDoc = await transaction.get(counterRef)
                        const today = new Date()
                        const year = today.getFullYear().toString()
                        const month = (today.getMonth() + 1).toString().padStart(2, '0')
                        const tanggalKode = `${year.slice(-2)}${month}`

                        let baseNumber
                        if (!counterDoc.exists() || counterDoc.data().lastResetYear !== year) {
                            baseNumber = 501
                        } else {
                            baseNumber = counterDoc.data().lastNumber + 1
                        }
                        const candidateNumber = baseNumber + offset

                        const sequence = candidateNumber.toString().padStart(7, '0')
                        const nomorBS = `BS${tanggalKode}${kodeUnitBisnis}${sequence}`

                        transaction.set(counterRef, {
                            lastNumber: candidateNumber,
                            lastResetYear: year
                        })

                        // Dicatat di transaksi yang sama supaya storage.rules bisa memvalidasi
                        // kepemilikan lampiran/PDF lewat firestore.get() cross-service (lihat 18.6)
                        transaction.set(doc(db, 'displayIdOwners', nomorBS), { uid: userData.uid })

                        transaction.set(newDocRef, {
                            ...bonSementaraData,
                            id: newDocRef.id,
                            displayId: nomorBS,
                            bonSementara: bonSementaraData.bonSementara.map((item) => ({
                                ...item,
                                nomorBS
                            }))
                        })

                        return nomorBS
                    })
                }

                let finalNomorBS = null
                let lastCollisionError = null
                const MAX_ATTEMPTS = 50
                for (let offset = 0; offset < MAX_ATTEMPTS; offset++) {
                    try {
                        finalNomorBS = await attemptGenerateBs(offset)
                        break
                    } catch (attemptError) {
                        // permission-denied di titik ini SANGAT mungkin karena
                        // nomor kandidat sudah dipakai (lihat catatan di atas) --
                        // coba nomor berikutnya. Error jenis LAIN (mis. memang
                        // sesi tidak valid) langsung dilempar, tidak perlu
                        // diulang 50x percuma.
                        if (attemptError?.code !== 'permission-denied') throw attemptError
                        lastCollisionError = attemptError
                    }
                }
                if (finalNomorBS === null) {
                    throw lastCollisionError || new Error(`Tidak menemukan nomor BS yang tersedia untuk unit ${kodeUnitBisnis} setelah ${MAX_ATTEMPTS} percobaan`)
                }

                toast.success(`Bon Sementara berhasil diajukan! Nomor: ${finalNomorBS}`)

                setAlreadyFetchBS(false) 
                resetForm()
                setIsSubmitting(false)
            }
        } catch (error) {
            // Bagian AL: log code & message terpisah (bukan cuma object error
            // yang di Chrome kadang collapsed/tidak informatif) supaya insiden
            // permission-denied berikutnya bisa langsung ketahuan penyebabnya
            // dari console tanpa perlu bongkar data Firestore manual.
            console.error('Error submitting bon sementara:', error?.code, error?.message, error)
            toast.error('Terjadi kesalahan saat menyimpan data. Silakan coba lagi.')
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container mx-auto py-10 md:py-8">
            <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                Ajukan <span className="font-bold">Bon Sementara</span>
            </h2>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow transition-colors">
                {/* --- Layout diseragamkan untuk semua role --- */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:gap-6 mb-2 lg:mb-3">
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nama Lengkap</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.nama}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Unit Bisnis <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={isAdmin ? BUSINESS_UNITS : userUnitOptions}
                            value={selectedUnit}
                            onChange={handleUnitChange}
                            placeholder="Pilih Unit Bisnis"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:gap-6 mb-2 lg:mb-3">
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nomor Rekening</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.accountNumber}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Reviewer 1 <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={reviewerOptions}
                            value={selectedReviewer1}
                            onChange={setSelectedReviewer1}
                            placeholder="Pilih Reviewer 1"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isDisabled={isSingleUnit} // <-- Di-disable jika unit cuma 1
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:gap-6 mb-2 lg:mb-3">
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nama Bank</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.bankName}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Reviewer 2 <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={reviewerOptions}
                            value={selectedReviewer2}
                            onChange={setSelectedReviewer2}
                            placeholder="Pilih Reviewer 2"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isDisabled={isSingleUnit} // <-- Di-disable jika unit cuma 1
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:gap-6 mb-2 lg:mb-3">
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Tanggal Pengajuan</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={formatDate(todayDate)}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Kategori BS <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={kategoriOptions}
                            value={selectedKategori}
                            onChange={handleKategoriChange}
                            placeholder="Pilih Kategori..."
                            className="w-full"
                            styles={customStyles}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                        />
                    </div>
                </div>

                <hr className="border-gray-300 dark:border-gray-600 my-6" />

                {bonSementara.map((bon, index) => (
                    <div
                        key={index}
                        className="flex flex-col xl:flex-row gap-2 mb-2"
                    >
                        <div className="flex flex-col md:flex-row gap-2 flex-1">
                            <div className="flex-1">
                                {index === 0 && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                        Nomor BS <span className="text-red-500">*</span>
                                    </label>
                                )}
                                <input
                                    className="w-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md h-10 px-4 py-2 cursor-not-allowed"
                                    type="text"
                                    value={bon.nomorBS}
                                    placeholder="Pilih Unit Bisnis untuk generate BS"
                                    disabled
                                    onChange={(e) => handleInputChange(index, "nomorBS", e.target.value)}
                                />
                            </div>

                            <div className="flex-1">
                                {index === 0 && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                        Jumlah BS <span className="text-red-500">*</span>
                                    </label>
                                )}
                                <input
                                    className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none h-10 px-4 py-2"
                                    type="text"
                                    value={formatRupiah(bon.jumlahBS)}
                                    onChange={(e) => handleInputChange(index, "jumlahBS", e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex-1">
                            {index === 0 && (
                                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                    Aktivitas (Keterangan) <span className="text-red-500">*</span>
                                </label>
                            )}
                            <textarea
                                className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none h-10 px-4 py-2 resize-none"
                                style={{scrollbarWidth: 'none'}}  
                                type="text"
                                value={bon.aktivitas}
                                onChange={(e) => handleInputChange(index, "aktivitas", e.target.value)}
                            />
                        </div>
                    </div>
                ))}

                {needsLampiran && (
                    <div className="mt-4">
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Lampiran {!hasExistingLampiran && <span className="text-red-500">*</span>}
                        </label>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                            BS di atas {formatRupiah(BS_LAMPIRAN_THRESHOLD)} wajib melampirkan dokumen pendukung (penawaran, invoice, dsb).
                            {hasExistingLampiran && ' Lampiran sebelumnya tetap dipakai kalau tidak mengunggah file baru.'}
                        </p>
                        <div className="flex flex-col xl:flex-row items-start xl:items-center w-full">
                            <input
                                type="file"
                                id="bs-file-upload"
                                className="hidden"
                                accept={ATTACHMENT_ACCEPT}
                                multiple
                                onChange={handleFileUpload}
                            />
                            <label
                                htmlFor="bs-file-upload"
                                className="w-full xl:w-fit text-center h-full xl:h-10 px-4 py-4 xl:py-2 bg-gray-50 dark:bg-gray-700 xl:bg-gray-200 dark:xl:bg-gray-700 border dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 hover:border-gray-400 transition duration-300 ease-in-out dark:text-gray-200"
                            >
                                Upload File
                            </label>
                            <span className="ml-0 xl:ml-4 text-gray-500 dark:text-gray-400 mt-2 xl:mt-0 text-sm">
                                Format .pdf/.jpg/.png, bisa lebih dari 1 file (Max Size: {Math.round(ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024))}MB/file)
                            </span>
                        </div>
                        {attachmentFiles.length > 0 && (
                            <div className="mt-3 w-full">
                                {attachmentFiles.map((file, index) => (
                                    <div key={index} className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded mb-2 border border-gray-200 dark:border-gray-600">
                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[80%]">{file.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(index)}
                                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 ml-2 font-bold"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {isEditMode && (
                    <div className="mt-4">
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Catatan Perubahan (Alasan Edit) <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none h-20 px-4 py-2"
                            placeholder="Jelaskan kesalahan/perubahan yang diperbaiki pada pengajuan ini, mis. 'Nominal Jumlah BS salah ketik, diperbaiki sesuai kuitansi asli'"
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                        />
                    </div>
                )}

                <hr className="border-gray-300 dark:border-gray-600 my-6" />

                <div className="flex justify-end mt-6">
                    <button
                        className={`w-full xl:w-fit rounded text-white py-3
                        ${isSubmitting ? 'px-8 bg-red-700 cursor-not-allowed' : 'px-16 bg-red-600 hover:bg-red-700 hover:text-gray-200'}
                        flex items-center justify-center relative`}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-1 text-gray-200">
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                    Submitting...
                                </>
                            </div>
                        ) : (
                            'Submit'
                        )}
                    </button>
                </div>
            </div>

        </div>
    )
}

export default FormBs